<template>
  <div class="container">
    <label class="checkbox-container" :class="[glowColor]">
      <div class="checkbox" :class="[backgroundColor]">
        <input
          :id="id"
          type="checkbox"
          :checked="checked"
          @change="$emit('valueChanged', $event)"
        />
        <svg
          class="checkmark"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20 6L9 17L4 12"
            :stroke="tintColor"
            stroke-width="5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <!-- <span class="checkmark"></span> -->
      </div>
    </label>
    <div v-if="title || description" class="text-container">
      <span class="">{{ title }}</span>
      <span>{{ description }}</span>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { computed, ref } from 'vue'

defineEmits<{
  (event: 'valueChanged', value: Event): void
}>()

const id = ref<string>(`checkbox-${Math.random()}`)

interface Props {
  title?: string
  description?: string
  color: string
  tintColor: string
  checkedColor?: string
  valid?: boolean | undefined
  error?: string | undefined
  checked?: boolean
  borderColor?: string
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  color: 'blue',
  tintColor: 'white',
  checkedColor: 'green', // Default checked color
  description: '',
  borderColor: 'grey',
  valid: undefined,
  error: undefined,
  checked: false
})

const color = computed(() => {
  return props.color
})

const tintColor = computed(() => {
  return props.tintColor
})

const backgroundColor = computed<string>(() => {
  return props.checked ? 'checked' : 'unchecked'
})

const glowColor = computed<string>(() => {
  if (props.error) return 'error'
  return 'default'
})

const checkedColor = computed(() => {
  return props.checked ? props.checkedColor : props.color
})
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';

.checked {
  background-color: v-bind(checkedColor);
}
.unchecked {
  background-color: v-bind(color);
}
input:focus + .background {
  background-color: v-bind(color);
}
.container {
  display: flex;
  flex-direction: row;
  width: '100%';
}
.checkbox-container {
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  height: 16px;
  width: 16px;
  background-color: variables.$color-background;
  &.error {
    &:focus-within {
      background-color: rgb(246, 146, 146);
    }
  }
  &.success {
    &:focus-within {
      background-color: rgb(146, 246, 164);
    }
  }
  &.default {
    &:focus-within {
      background-color: variables.$color-graph-lightblue;
    }
  }
  .checkbox {
    z-index: 2;
    display: block;
    position: relative;
    border-radius: 5px;
    border-width: 1px;
    border-style: solid;
    border-color: v-bind(borderColor);
    height: 16px;
    width: 16px;
    cursor: pointer;
    font-size: 22px;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    &.error {
      border-color: red;
    }
    &.success {
      border-color: green;
    }
    &.checked {
      border-color: v-bind(checkedColor);
    }
    &.default {
      border-color: v-bind(tintColor);
    }
  }
  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }
  .checkmark {
    position: absolute;
    display: none;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    height: 12px;
    width: 12px;
    border-radius: 4px;
  }
  input:checked ~ .checkmark {
    display: block;
  }
}
.text-container {
  display: flex;
  flex: 1;
  margin-left: 8px;
  flex-direction: column;
  justify-content: flex-start;
  :nth-child(1) {
    @include font-mixins.desktop-bold-paragraph;
    color: variables.$color-text-primary;
    &.success {
      color: green;
    }
  }
  :nth-child(2) {
    @include font-mixins.desktop-regular-paragraph;
    color: variables.$color-text-secondary;
  }
}
</style>
